import { recordingModeTarget } from "@/api";
import type {
  ConfigLabelTarget,
  ConfigRecordingMode,
  DataOrigin,
  MemberRef,
  ConfigurationView,
  DatasetConfigurationPutReq,
} from "@/api";
import { normalizeLabelText } from "@/components/label/labelText";

/**
 * 데이터셋 리뷰 draft 의 순수 코어.
 *
 * 리뷰 화면은 embodiment·tip·label 을 하나의 full target 으로 저장한다. 이 모듈은 그 draft
 * shape 과 순수 전이만 소유한다. 상세 direct 편집과 리뷰 후보 편집이 같은 규칙을 쓴다(단일 SoT).
 * React·서버 호출·revision·윈도우 상태는 소유하지 않는다.
 *
 * Owns:
 * - 편집 draft 의 shape(embodiment/tip 선택 + label 셋)과 스냅샷을 draft 로 바꾸는 변환.
 * - 순수 리듀서(embodiment/tip 설정, label 추가·삭제·키별 치환).
 * - PUT payload 조립(uuid / key·value target), dirty 판정, 중복 검증.
 *
 * Does not own:
 * - 서버 호출·낙관적 동시성(revision)·409 처리. 컨트롤러가 소유한다.
 * - 윈도우 lifecycle·dirty 이탈 가드, 라벨 입력 컨트롤 렌더.
 */

// draft 의 embodiment/tip 선택. 표시(name/tag)와 write(uuid)를 함께 담는다. null 은 미선택이다
// (embodiment=서버 sentinel unspecified, tip=없음).
export type DraftEmbodiment = { uuid: string; name: string } | null;
export type DraftTip = { uuid: string; name: string; tag: string | null } | null;

// draft label. 기존 카탈로그 라벨(uuid 지정) 또는 새 key/value(서버 get-or-create 대상)다.
export type DraftLabel =
  | { kind: "existing"; uuid: string; key: string; value: string; color: number }
  | { kind: "new"; key: string; value: string };

export type DraftConfiguration = {
  displayName: string;
  operatorMember: MemberRef | null;
  dataOrigin: DataOrigin | null;
  recordingMode: ConfigRecordingMode;
  embodiment: DraftEmbodiment;
  tip: DraftTip;
  labels: readonly DraftLabel[];
};

export const EMPTY_DRAFT_CONFIGURATION: DraftConfiguration = {
  displayName: "",
  operatorMember: null,
  dataOrigin: null,
  recordingMode: { value: null, modified_manually: false },
  embodiment: null,
  tip: null,
  labels: [],
};

// 라벨 식별자(정규화 key/value). 서버 get-or-create 와 같은 정규화를 쓴다. 그래서 기존 라벨과 새
// 라벨의 key·value 가 같으면 같은 라벨로 취급한다(uuid 기준이 아님).
export function labelSignature(label: { key: string; value: string }): string {
  // 구분자는 라벨 텍스트에 없는 null 문자다. "a b"+"c" 와 "a"+"b c" 의 서명 충돌을 막는다.
  return normalizeLabelText(label.key) + "\u0000" + normalizeLabelText(label.value);
}

// active·review 스냅샷(ConfigurationView)을 편집 draft 로 바꾼다. null 이면 빈 draft(신규 편집)다.
export function draftFromConfiguration(view: ConfigurationView | null): DraftConfiguration {
  if (view === null) {
    return EMPTY_DRAFT_CONFIGURATION;
  }
  return {
    displayName: view.display_name,
    operatorMember: view.operator_member,
    dataOrigin: view.data_origin,
    recordingMode: view.recording_mode,
    embodiment: view.embodiment ? { uuid: view.embodiment.uuid, name: view.embodiment.name } : null,
    tip: view.tip ? { uuid: view.tip.uuid, name: view.tip.name, tag: view.tip.tag } : null,
    labels: view.labels.map((label) => ({
      kind: "existing" as const,
      uuid: label.uuid,
      key: label.key,
      value: label.value,
      color: label.color,
    })),
  };
}

// ── 순수 리듀서 ──────────────────────────────────────────────────────────

export function withEmbodiment(
  draft: DraftConfiguration,
  embodiment: DraftEmbodiment,
): DraftConfiguration {
  return { ...draft, embodiment };
}

export function withTip(draft: DraftConfiguration, tip: DraftTip): DraftConfiguration {
  return { ...draft, tip };
}

export function withLabelAdded(draft: DraftConfiguration, label: DraftLabel): DraftConfiguration {
  return { ...draft, labels: [...draft.labels, label] };
}

// 같은 라벨(정규화 key/value)의 첫 항목을 제거한다. `그 외 라벨` 칩 목록 삭제에 쓴다.
export function withLabelRemoved(draft: DraftConfiguration, label: DraftLabel): DraftConfiguration {
  const target = labelSignature(label);
  let removed = false;
  const labels = draft.labels.filter((entry) => {
    if (!removed && labelSignature(entry) === target) {
      removed = true;
      return false;
    }
    return true;
  });
  return { ...draft, labels };
}

// 특정 key 의 라벨 전체를 next 로 치환한다. reserved env/source/task 편집의 단일·다중·비움에
// 함께 쓴다.
export function withLabelsForKey(
  draft: DraftConfiguration,
  key: string,
  next: readonly DraftLabel[],
): DraftConfiguration {
  const normalizedKey = normalizeLabelText(key);
  const others = draft.labels.filter((label) => normalizeLabelText(label.key) !== normalizedKey);
  return { ...draft, labels: [...others, ...next] };
}

// 특정 key 의 draft 라벨(정규화 비교). reserved 컨트롤 prefill·`그 외 라벨` 분리에 쓴다.
export function labelsForKey(draft: DraftConfiguration, key: string): readonly DraftLabel[] {
  const normalizedKey = normalizeLabelText(key);
  return draft.labels.filter((label) => normalizeLabelText(label.key) === normalizedKey);
}

// ── payload·dirty·검증 ───────────────────────────────────────────────────

function labelTarget(label: DraftLabel): ConfigLabelTarget {
  return label.kind === "existing"
    ? { label_uuid: label.uuid }
    : { key: label.key, value: label.value };
}

// draft 를 PUT/confirm payload 로 바꾼다. expectedRevision 은 baseline pair 의 현재 revision
// 이다(낙관적 동시성).
export function toPutPayload(
  draft: DraftConfiguration,
  expectedRevision: number,
): DatasetConfigurationPutReq {
  return {
    expected_revision: expectedRevision,
    configuration: {
      display_name: draft.displayName.trim(),
      operator_member: draft.operatorMember ? { uuid: draft.operatorMember.uuid } : null,
      data_origin: draft.dataOrigin,
      recording_mode: recordingModeTarget(draft.recordingMode),
      embodiment: draft.embodiment ? { uuid: draft.embodiment.uuid } : null,
      tip: draft.tip ? { uuid: draft.tip.uuid } : null,
      labels: draft.labels.map(labelTarget),
    },
  };
}

// 순서와 무관한 설정 서명. embodiment/tip uuid 와 라벨 셋(정규화)으로 만든다. dirty 판정과 비교에 쓴다.
export function configurationSignature(draft: DraftConfiguration): string {
  const embodiment = draft.embodiment?.uuid ?? "";
  const tip = draft.tip?.uuid ?? "";
  const labels = draft.labels.map(labelSignature).slice().sort().join("|");
  return JSON.stringify([
    draft.displayName.trim(),
    draft.operatorMember?.uuid ?? null,
    draft.dataOrigin,
    recordingModeTarget(draft.recordingMode),
    embodiment,
    tip,
    labels,
  ]);
}

// draft 가 baseline(직전 조회 스냅샷의 draft)과 의미상 다른지 판정한다. 정렬과 정규화로 서버
// no-op 규칙과 맞춘다.
export function isDirty(draft: DraftConfiguration, baseline: DraftConfiguration): boolean {
  return configurationSignature(draft) !== configurationSignature(baseline);
}

// 정규화 후 같은 라벨이 둘 이상이면 그 중복(2번째부터)을 반환한다. 저장 전에 label_duplicate 를
// 미리 막는 데 쓴다.
export function findDuplicateLabels(draft: DraftConfiguration): readonly DraftLabel[] {
  const seen = new Set<string>();
  const duplicates: DraftLabel[] = [];
  for (const label of draft.labels) {
    const signature = labelSignature(label);
    if (seen.has(signature)) {
      duplicates.push(label);
    } else {
      seen.add(signature);
    }
  }
  return duplicates;
}

// 값이 없는 축의 표시. 빈 문자열을 그대로 두면 화면에서 무엇이 바뀌었는지 알 수 없다.
const EMPTY_DISPLAY = "—";

/** 팁 한 줄 표시(`name` 또는 `name:tag`). 미선택이면 빈 값 표기. */
export function formatDraftTip(draft: DraftConfiguration): string {
  const tip = draft.tip;
  if (tip === null) {
    return EMPTY_DISPLAY;
  }
  return tip.tag !== null ? `${tip.name}:${tip.tag}` : tip.name;
}

/** 라벨 한 줄 표시. 순서가 비교 결과에 영향을 주지 않게 정렬한다. */
export function formatDraftLabels(draft: DraftConfiguration): string {
  if (draft.labels.length === 0) {
    return EMPTY_DISPLAY;
  }
  return draft.labels
    .map((label) => `${label.key}: ${label.value}`)
    .slice()
    .sort()
    .join(", ");
}
